/**
 * Status codes that should trigger automatic retry with exponential backoff
 * - 403: Cloudflare challenge / Forbidden (temporary)
 * - 408: Request Timeout
 * - 429: Too Many Requests (rate limiting)
 * - 502: Bad Gateway (temporary gateway issues)
 * - 503: Service Unavailable (temporary unavailability)
 * - 504: Gateway Timeout
 */
const RETRIABLE_STATUS_CODES = [403, 408, 429, 502, 503, 504];

/**
 * Global registry to store modal instances by their element ID
 * This allows us to access modal instances that were initialized by the generic component initialization
 */
declare global {
    interface Window {
        __aceModalRegistry?: Map<string, any>;
    }
}

/**
 * Error display utility for showing fatal connection errors
 * Looks for the modal element with ID 'fatal-connection-error-modal' and shows it
 * The modal should be auto-initialized by generic component initialization (since it has data-modal attribute)
 */
export function showFatalConnectionError(): void {
    const modalElement = document.getElementById('fatal-connection-error-modal');
    
    // If modal element not found, do nothing (graceful failure)
    if (!modalElement) {
        console.log('Fatal connection error modal not found in DOM. Modal may not be rendered on this page.');
        return;
    }
    
    console.log('Fatal connection error modal element found, attempting to show...');
    
    // Try to get the modal instance from the global registry
    // The Modal class should register itself when initialized
    if (window.__aceModalRegistry) {
        const modalInstance = window.__aceModalRegistry.get('fatal-connection-error-modal');
        console.log('Modal instance from registry:', modalInstance);
        if (modalInstance && modalInstance.modal && typeof modalInstance.modal.show === 'function') {
            try {
                modalInstance.modal.show();
                console.warn('Fatal connection error detected. User advised to refresh page.');
                return;
            } catch (error) {
                console.error('Failed to show fatal connection error modal:', error);
            }
        } else {
            console.log('Modal instance not found in registry or show method not available');
        }
    } else {
        console.log('Modal registry not available');
    }
    
    // Fallback: Try to find existing trigger buttons with data-a11y-dialog-show attribute
    const existingTriggers = document.querySelectorAll('[data-a11y-dialog-show="fatal-connection-error-modal"]');
    if (existingTriggers.length > 0) {
        try {
            (existingTriggers[0] as HTMLElement).click();
            console.warn('Fatal connection error detected. User advised to refresh page.');
            return;
        } catch (error) {
            console.error('Failed to trigger modal via existing trigger:', error);
        }
    }
    
    // Last resort: Directly manipulate DOM attributes (simulating what a11y-dialog does)
    // This is less ideal but should work if the modal structure is correct
    try {
        // Remove aria-hidden to show the modal
        modalElement.removeAttribute('aria-hidden');
        modalElement.setAttribute('aria-modal', 'true');
        
        // Hide siblings (targets) by setting aria-hidden on them
        // This is what a11y-dialog does to disable background content
        const parent = modalElement.parentElement;
        if (parent) {
            const siblings = Array.from(parent.children).filter(child => child !== modalElement);
            siblings.forEach(sibling => {
                if (!sibling.hasAttribute('aria-hidden')) {
                    sibling.setAttribute('aria-hidden', 'true');
                }
            });
        }
        
        // Focus the first focusable element in the modal
        const firstFocusable = modalElement.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement;
        if (firstFocusable) {
            firstFocusable.focus();
        }
        
        // Lock body scroll (prevent background scrolling)
        document.body.style.overflow = 'hidden';
        
        console.warn('Fatal connection error detected. User advised to refresh page.');
    } catch (error) {
        console.error('Failed to show fatal connection error modal via DOM manipulation:', error);
    }
}

/**
 * Configuration for retry behavior
 */
export interface RetryConfig {
    maxRetries?: number;
    baseDelay?: number;
}

/**
 * Enhanced fetch function that automatically retries transient errors with exponential backoff
 * @param input - Request info (URL or Request object)
 * @param init - Fetch initialization options
 * @param retryConfig - Optional retry configuration
 * @returns Promise that resolves with Response
 */
export async function enhancedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
    retryConfig?: RetryConfig
): Promise<Response> {
    const maxRetries = retryConfig?.maxRetries ?? 3;
    const baseDelay = retryConfig?.baseDelay ?? 1000;
    let retryCount = 0;

    // Preserve the request configuration including body (clone if needed)
    const preservedInit: RequestInit = { ...init };
    
    // If body is a string (most common case), preserve it directly
    // For other types, we'll use it as-is but note that streams/FormData may not work for retries
    if (init?.body && typeof init.body === 'string') {
        preservedInit.body = init.body;
    } else if (init?.body) {
        // For other body types (FormData, Blob, etc.), try to preserve
        // Note: This may not work perfectly for all body types, but preserves the reference
        preservedInit.body = init.body;
    }

    const makeRequest = async (): Promise<Response> => {
        const response = await fetch(input, preservedInit);
        
        if (RETRIABLE_STATUS_CODES.includes(response.status) && retryCount < maxRetries) {
            retryCount++;
            const delay = baseDelay * Math.pow(2, retryCount - 1);
            console.warn(`${response.status} error encountered, retry ${retryCount}/${maxRetries} after ${delay}ms`);
            
            await new Promise(resolve => setTimeout(resolve, delay));
            
            return makeRequest();
        }
        
        if (RETRIABLE_STATUS_CODES.includes(response.status) && retryCount >= maxRetries) {
            console.error(`${response.status} error persisted after ${maxRetries} retries. Showing fatal error message.`);
            showFatalConnectionError();
        }
        
        return response;
    };

    return makeRequest();
}

